"use client";

import { AppWindow, Download, MoreVertical, type LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import type {
  DesktopInstallGuide,
  DesktopInstallStepIcon,
} from "@award-party/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STEP_ICONS: Record<DesktopInstallStepIcon, LucideIcon> = {
  download: Download,
  more: MoreVertical,
  dock: AppWindow,
};

const PLATFORM_KEY = {
  "desktop-chromium": "chromium",
  "desktop-safari": "safari",
  "desktop-other": "other",
} as const;

interface DesktopInstallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guide: DesktopInstallGuide;
  canNativeInstall: boolean;
  onNativeInstall: () => void;
}

function installCopy(
  guide: DesktopInstallGuide,
  t: ReturnType<typeof useTranslations<"DesktopInstall">>,
) {
  const platformKey = PLATFORM_KEY[guide.platform];
  if (platformKey === "chromium") {
    return {
      headline: t("chromium.headline"),
      description: t("chromium.description"),
      steps: guide.steps.map((step) =>
        step.id === "addressBar"
          ? {
              ...step,
              title: t("chromium.addressBarTitle"),
              body: t("chromium.addressBarBody"),
            }
          : {
              ...step,
              title: t("chromium.menuTitle"),
              body: t("chromium.menuBody"),
            },
      ),
    };
  }
  if (platformKey === "safari") {
    return {
      headline: t("safari.headline"),
      description: t("safari.description"),
      steps: guide.steps.map((step) => ({
        ...step,
        title: t("safari.dockTitle"),
        body: t("safari.dockBody"),
      })),
    };
  }
  return {
    headline: t("other.headline"),
    description: t("other.description"),
    steps: guide.steps.map((step) => ({
      ...step,
      title: t("other.menuTitle"),
      body: t("other.menuBody"),
    })),
  };
}

export function DesktopInstallDialog({
  open,
  onOpenChange,
  guide,
  canNativeInstall,
  onNativeInstall,
}: DesktopInstallDialogProps) {
  const t = useTranslations("DesktopInstall");
  const copy = installCopy(guide, t);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(90vh,40rem)] overflow-y-auto rounded-2xl border border-(--mk-cream-08) bg-(--mk-ink-soft) text-(--mk-cream) shadow-[0_24px_64px_rgba(0,0,0,0.7)] sm:max-w-md">
        <DialogHeader>
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-xl border border-(--mk-gold-border) bg-(--mk-gold-bg)">
            <Download className="h-5 w-5 text-(--mk-gold)" />
          </div>
          <DialogTitle className="font-serif text-xl text-(--mk-cream)">
            {copy.headline}
          </DialogTitle>
          <DialogDescription className="text-(--mk-cream-35)">
            {copy.description}
          </DialogDescription>
        </DialogHeader>

        {canNativeInstall ? (
          <button
            type="button"
            onClick={onNativeInstall}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-xl bg-(--mk-gold) px-4 text-sm font-semibold text-(--mk-ink) transition-colors hover:bg-(--mk-gold-light) disabled:cursor-not-allowed"
          >
            {t("installButton")}
          </button>
        ) : null}

        <ol className="space-y-3">
          {copy.steps.map((step, index) => {
            const Icon = STEP_ICONS[step.icon];
            return (
              <li
                key={step.id}
                className="flex gap-3 rounded-xl border border-(--mk-cream-08) bg-(--mk-ink) p-3"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-(--mk-gold-bg) text-xs font-bold text-(--mk-gold)">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-(--mk-cream)">
                    <Icon className="h-4 w-4 shrink-0 text-(--mk-gold)" aria-hidden />
                    {step.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-(--mk-cream-35)">
                    {step.body}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
